/** Time parsing/formatting — verbatim ports from the reference app. */

/** "HH:MM" → minutes since midnight */
export function pmin(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

/** 24h → "h:MM AM/PM" with a narrow no-break space before AM/PM */
export function h12(h: number | string, m: number | string): string {
  let hh24 = ((+h % 24) + 24) % 24;
  const ap = hh24 < 12 ? "AM" : "PM";
  let hh = hh24 % 12;
  if (hh === 0) hh = 12;
  return hh + ":" + String(m).padStart(2, "0") + " " + ap;
}

/** Rewrites every bare "HH:MM" in a string to 12-hour form (leaves existing AM/PM alone). */
export function t12(s: string | undefined | null): string {
  return (s || "").replace(/\b([01]?\d|2[0-4]):([0-5]\d)\b(?![\s ]?[AP]M)/g, (_x, h, m) => h12(h, m));
}

/** minutes → 12-hour label, wrapping mod 1440 */
export function fmin(min: number): string {
  min = ((min % 1440) + 1440) % 1440;
  return h12(Math.floor(min / 60), Math.round(min % 60));
}

/** duration in hours → "skip" / "2½ hr" / "full day (~N h)" */
export function fdur(h: number): string {
  if (h === 0) return "skip";
  if (h >= 6) return "full day (~" + Math.round(h) + " h)";
  const whole = Math.floor(h);
  const frac = h - whole;
  let s = whole ? whole + "" : "";
  if (Math.abs(frac - 0.5) < 0.01) s += "½";
  else if (Math.abs(frac - 0.25) < 0.01) s += "¼";
  else if (Math.abs(frac - 0.75) < 0.01) s += "¾";
  return s + (h < 1 ? " hr" : " hr");
}

/**
 * HTML-escape + 12-hour rewrite. Escapes QUOTES as well as the angle bracket
 * and ampersand, because this output goes into attributes as often as into
 * text: every `data-tip="${esc(note)}"` in the UI is an attribute context,
 * and there a bare `"` ends the attribute early and lets the rest of the
 * value become markup — stored XSS from any co-planner's note or any
 * AI-proposed text a human merely looks at. Escaping only `&` and `<` is
 * sufficient for text nodes and silently insufficient one character to the
 * left of a quote, so this does the complete set and callers stop having to
 * know which context they're in.
 *
 * The 12-hour rewrite runs FIRST and escaping last: entities introduced here
 * must never be re-scanned by t12's digit matching.
 */
export function esc(s: string | undefined | null): string {
  return t12(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * A URL safe to put in an href attribute: http(s) only, quotes neutralized.
 * Anything else — javascript:, data:, a bare string — renders as no link at
 * all. Curated data never trips this; user- and AI-entered data must not get
 * to choose its own scheme.
 */
export function safeUrl(u: string | undefined | null): string | null {
  const s = (u || "").trim();
  if (!/^https?:\/\//i.test(s)) return null;
  return s.replace(/"/g, "%22").replace(/</g, "%3C");
}
